import React from 'react';

/**
 * Define a interface para as propriedades de cada coluna da tabela.
 * T é um tipo genérico que representa o formato dos objetos de dados em cada linha.
 */
interface Coluna<T extends Record<string, any>> {
  /**
   * O título da coluna a ser exibido no cabeçalho da tabela.
   */
  titulo: string;
  /**
   * A chave do objeto de dados que corresponde ao valor a ser exibido nesta coluna.
   * Por exemplo, se `dados` contém `{ nome: 'João', idade: 30 }`,
   * a `chave` pode ser 'nome' ou 'idade'.
   */
  chave: string;
  /**
   * Uma função de renderização opcional para personalizar a exibição do conteúdo da célula.
   * Recebe o item completo da linha e deve retornar um ReactNode.
   * Se fornecida, esta função será usada para renderizar a célula, ignorando a `chave` para o conteúdo.
   */
  render?: (item: T) => React.ReactNode;
  /**
   * Classes CSS adicionais para o cabeçalho desta coluna.
   * Permite estilização específica para o `<th>` desta coluna.
   */
  headerClassName?: string;
  /**
   * Classes CSS adicionais para as células de dados desta coluna.
   * Permite estilização específica para o `<td>` desta coluna em todas as linhas.
   */
  cellClassName?: string;
}

/**
 * Define a interface para as propriedades do componente Table.
 *
 * @template T O tipo dos objetos de dados que serão exibidos em cada linha da tabela.
 *             Deve ser um objeto com chaves de string e quaisquer valores.
 */
interface TableProps<T extends Record<string, any>> {
  /**
   * Um array de objetos que definem as colunas da tabela.
   * Cada objeto deve ter um `titulo` (para o cabeçalho) e uma `chave` (para acessar os dados).
   */
  colunas: Coluna<T>[];
  /**
   * Um array de objetos que representam as linhas da tabela.
   * Cada objeto deve ter propriedades que correspondem às `chaves` definidas nas `colunas`.
   */
  dados: T[];
  /**
   * Classes CSS adicionais para o container principal da tabela.
   * Útil para aplicar estilos personalizados ao redor da tabela, como largura ou margens.
   */
  className?: string;
  /**
   * Mensagem a ser exibida quando não houver dados na tabela.
   * O valor padrão é "Nenhum dado encontrado.".
   */
  emptyMessage?: string;
  /**
   * Função de callback opcional chamada ao clicar em uma linha da tabela.
   * Recebe o item de dados completo da linha clicada.
   * Se fornecida, as linhas terão um estilo de cursor de ponteiro e um efeito de hover,
   * indicando que são clicáveis.
   */
  onRowClick?: (item: T) => void;
}

/**
 * Componente de Tabela genérico e estilizado para exibir dados de forma moderna e responsiva.
 * Utiliza Tailwind CSS para um visual admirável e UX/UI impecável, seguindo o Design System do projeto.
 * Suporta colunas configuráveis, renderização personalizada de células e interação com linhas.
 *
 * @template T O tipo dos objetos de dados que serão exibidos em cada linha da tabela.
 *             Deve ser um objeto com chaves de string e quaisquer valores.
 *
 * @param {TableProps<T>} props As propriedades para configurar a tabela.
 * @returns {JSX.Element} Um elemento JSX que representa a tabela.
 */
const Table = <T extends Record<string, any>>({
  colunas,
  dados,
  className = '',
  emptyMessage = 'Nenhum dado encontrado.',
  onRowClick,
}: TableProps<T>): JSX.Element => {
  return (
    <div className={`overflow-x-auto shadow-lg rounded-lg border border-primary/20 ${className}`}>
      <table className="min-w-full divide-y divide-secondary/30">
        <thead className="bg-primary sticky top-0 z-10">
          <tr>
            {colunas.map((coluna, index) => (
              <th
                key={`header-${coluna.chave}-${index}`} // Chave única para o cabeçalho
                scope="col"
                className={`px-6 py-3 text-left text-xs font-bold text-background uppercase tracking-wider ${coluna.headerClassName || ''}`}
              >
                {coluna.titulo}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-background divide-y divide-secondary/20">
          {dados.length === 0 ? (
            <tr>
              <td colSpan={colunas.length} className="px-6 py-8 text-center text-text/70 text-sm italic">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            dados.map((item, rowIndex) => (
              <tr
                key={item.id || `row-${rowIndex}`} // Usa item.id se disponível, senão um índice para a chave da linha
                className={`
                  ${onRowClick ? 'cursor-pointer hover:bg-gradient-to-r from-[#c5a663] via-[#f2dd82] to-[#c5a663]/20 transition-colors duration-200' : ''}
                  ${rowIndex % 2 === 0 ? 'bg-background' : 'bg-background/80'}
                `}
                onClick={() => onRowClick && onRowClick(item)}
              >
                {colunas.map((coluna, colIndex) => (
                  <td
                    key={`cell-${coluna.chave}-${colIndex}`} // Chave única para a célula
                    className={`px-6 py-4 whitespace-nowrap text-sm text-text ${coluna.cellClassName || ''}`}
                  >
                    {coluna.render
                      ? coluna.render(item)
                      : (item[coluna.chave as keyof T] ?? 'N/A')} {/* Acessa o valor ou 'N/A' se undefined/null */}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default Table;